import type { PropsWithChildren, ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  trigger?: ReactNode;
  // Para modales realmente obligatorios (p.ej. completar el perfil antes de poder usar el
  // panel): oculta la X y evita cerrar con Escape/clic fuera.
  blocking?: boolean;
  // "md" (por defecto) es el ancho de siempre; "lg" es para contenido que se queda corto ahí
  // (p.ej. el detalle de un partido, con vídeo + marcador lado a lado).
  size?: "md" | "lg";
}

const SIZE_CLASSES = { md: "max-w-lg", lg: "max-w-3xl" } as const;

export function Modal({ open, onOpenChange, title, description, trigger, blocking, size = "md", children }: PropsWithChildren<ModalProps>) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
        <Dialog.Content
          onEscapeKeyDown={(e) => blocking && e.preventDefault()}
          onPointerDownOutside={(e) => blocking && e.preventDefault()}
          onInteractOutside={(e) => blocking && e.preventDefault()}
          className={`fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-2rem)] ${SIZE_CLASSES[size]} -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-white shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95`}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 p-6 pb-5">
            <div>
              <Dialog.Title className="text-lg font-bold text-ink-900">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-ink-400">{description}</Dialog.Description>
              )}
            </div>
            {!blocking && (
              <Dialog.Close className="shrink-0 rounded-lg p-1.5 text-ink-300 transition-colors hover:bg-ink-50 hover:text-ink-600">
                <X className="h-4 w-4" />
              </Dialog.Close>
            )}
          </div>
          <div className="min-h-0 overflow-y-auto px-6 pb-6">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
